import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { defaultIndicatorLabels } from "./i18n-defaults";
// Abbreviated labels for each series type (shown in pane header chips)
const SERIES_LABELS = {
    Candlestick: "Candle",
    HollowCandle: "Hollow",
    OHLC: "OHLC",
    HeikinAshi: "HA",
    Line: "Line",
    Area: "Area",
    EMA: "EMA",
    BollingerBand: "BB",
    Volume: "Vol",
    Whale: "Whale",
    CVDApprox: "CVD",
    CVDRealtime: "CVD Real",
    RSI: "RSI",
    MACD: "MACD",
    StrengthElder: "Elder",
    StrengthRelative: "RelStr",
    KDJ: "KDJ",
    CCI: "CCI",
    DMI: "DMI",
    BIAS: "BIAS",
    BRAR: "BRAR",
    MTM: "MTM",
    EMV: "EMV",
    AO: "AO",
    ROC: "ROC",
    TRIX: "TRIX",
    DMA: "DMA",
    PVT: "PVT",
    PSY: "PSY",
    CR: "CR",
};
function paramNumber(params, key, fallback) {
    const value = params?.[key];
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function seriesLabel(type, params) {
    // Legacy alias: BollingerBands (plural) renders exactly like BollingerBand.
    const canonical = (type === "BollingerBands" ? "BollingerBand" : type);
    const base = SERIES_LABELS[canonical] ?? SERIES_LABELS[type] ?? type;
    type = canonical;
    if (type === "EMA")
        return `${base} (${paramNumber(params, "period", 20)})`;
    if (type === "RSI")
        return `${base} (${paramNumber(params, "period", 14)})`;
    if (type === "BollingerBand")
        return `${base} (${paramNumber(params, "period", 20)},${paramNumber(params, "stdDev", 2)})`;
    if (type === "MACD")
        return `${base} (${paramNumber(params, "fast", 12)},${paramNumber(params, "slow", 26)},${paramNumber(params, "signal", 9)})`;
    if (type === "KDJ")
        return `${base} (${paramNumber(params, "period", 9)},${paramNumber(params, "m1", 3)},${paramNumber(params, "m2", 3)})`;
    if (type === "CCI")
        return `${base} (${paramNumber(params, "period", 20)})`;
    if (type === "DMI")
        return `${base} (${paramNumber(params, "period", 14)})`;
    if (type === "BIAS")
        return `${base} (${paramNumber(params, "period", 6)})`;
    if (type === "BRAR")
        return `${base} (${paramNumber(params, "period", 26)})`;
    if (type === "MTM")
        return `${base} (${paramNumber(params, "period", 6)})`;
    if (type === "EMV")
        return `${base} (${paramNumber(params, "period", 14)})`;
    if (type === "ROC")
        return `${base} (${paramNumber(params, "period", 12)})`;
    if (type === "TRIX")
        return `${base} (${paramNumber(params, "period", 12)})`;
    if (type === "DMA")
        return `${base} (${paramNumber(params, "fastPeriod", 10)},${paramNumber(params, "slowPeriod", 50)})`;
    if (type === "PSY")
        return `${base} (${paramNumber(params, "period", 12)})`;
    if (type === "CR")
        return `${base} (${paramNumber(params, "period", 26)})`;
    if (type === "Whale")
        return `${base} (${paramNumber(params, "threshold", 50_000)})`;
    return base;
}
// Price-chart primary series: shown in the chip strip only for non-pinned panes.
// For the pinned (price) pane, price type chips are hidden since chart type is
// controlled separately by the chart type button in the topbar.
const PRIMARY_CHART_TYPES = [
    "Candlestick", "HollowCandle", "OHLC", "HeikinAshi", "Line", "Area",
];
export function IndicatorLegend({ pane, onToggleSeries, onRemoveSeries, onEditSeries, icons, className, style, labels, locale, seriesSuffix, seriesValue, embedded, visibleTypes, hiddenTypes }) {
    // eslint-disable-next-line no-restricted-globals -- SSR guard + locale detection fallback (S9 pattern).
    const resolvedLocale = locale ?? (typeof document !== "undefined" && document.documentElement.lang?.toLowerCase().startsWith("en") ? "en" : "vi");
    const _dl = defaultIndicatorLabels(resolvedLocale);
    // For the price pane: exclude primary chart types (controlled by topbar chart type picker)
    // For other panes: show all series
    const chips = pane.pinned
        ? pane.series.filter((s) => !PRIMARY_CHART_TYPES.includes(s.type))
        : pane.series;
    // DECLUTTER_V4: render-only filter — indices below still come from the
    // full pane.series, so suffix/value mapping cannot shift.
    const shown = chips.filter((s) => {
        const type = String(s.type);
        if (visibleTypes && !visibleTypes.includes(type))
            return false;
        if (hiddenTypes?.includes(type))
            return false;
        return true;
    });
    if (shown.length === 0)
        return null;
    return (_jsx("div", { className: `${embedded ? "rsc-pane-header__legend" : "rsc-indicator-legend"}${className ? ` ${className} ` : ""}`, style: embedded ? undefined : style, children: shown.map((series) => {
            const seriesIndex = pane.series.indexOf(series);
            const hidden = series.visible === false;
            const shortLabel = labels?.seriesLabel?.(series.type, series.params)
                ?? (series.type === "Comparison" ? String(series.overlaySymbolId ?? "Overlay") : undefined)
                ?? seriesLabel(series.type, series.params);
            const suffix = seriesSuffix?.(seriesIndex);
            const liveValue = seriesValue?.(series.type, seriesIndex);
            const chipStyle = series.color
                ? { "--chip-color": series.color }
                : undefined;
            return (_jsxs("div", { className: `rsc-indicator-chip${hidden ? " rsc-indicator-chip--hidden" : ""}`, style: chipStyle, children: [_jsx("span", { className: "rsc-indicator-chip__dot", "aria-hidden": "true" }), _jsxs("span", { className: "rsc-indicator-chip__name", children: [shortLabel, suffix !== undefined ? _jsx("span", { className: "rsc-indicator-chip__suffix", children: suffix }) : null, liveValue !== undefined ? (_jsx("span", { className: "rsc-indicator-chip__value", style: liveValue.color ? { color: liveValue.color } : undefined, children: liveValue.text })) : null] }), _jsx("button", { type: "button", className: "rsc-indicator-chip__btn rsc-indicator-chip__btn--eye", onClick: () => onToggleSeries(series.type, seriesIndex), title: hidden ? labels?.showIndicatorTitle ?? _dl.showIndicatorTitle : labels?.hideIndicatorTitle ?? _dl.hideIndicatorTitle, "aria-label": hidden ? labels?.showIndicatorAriaLabel?.(shortLabel) ?? _dl.showIndicatorAriaLabel(shortLabel) : labels?.hideIndicatorAriaLabel?.(shortLabel) ?? _dl.hideIndicatorAriaLabel(shortLabel), "aria-pressed": !hidden, children: hidden ? (icons?.eyeOff ?? "◌") : (icons?.eye ?? "👁") }), onEditSeries && !embedded ? (_jsx("button", { type: "button", className: "rsc-indicator-chip__btn rsc-indicator-chip__btn--edit", onClick: () => onEditSeries(series.type, seriesIndex), title: labels?.editIndicatorTitle ?? _dl.editIndicatorTitle, "aria-label": labels?.editIndicatorAriaLabel?.(shortLabel) ?? _dl.editIndicatorAriaLabel(shortLabel), children: icons?.edit ?? "⚙" })) : null, !embedded && (_jsx("button", { type: "button", className: "rsc-indicator-chip__btn rsc-indicator-chip__btn--remove", onClick: () => onRemoveSeries(series.type, seriesIndex), title: labels?.removeIndicatorTitle ?? _dl.removeIndicatorTitle, "aria-label": labels?.removeIndicatorAriaLabel?.(shortLabel) ?? _dl.removeIndicatorAriaLabel(shortLabel), children: icons?.remove ?? "×" }))] }, `${series.type}-${seriesIndex}`));
        }) }));
}
